import { useState, useCallback, useMemo } from 'react';
import { StarRating } from './StarRating';
import { ReviewTextFields } from './ReviewTextFields';
import { ReviewPhotoUpload, type ReviewPhoto } from './ReviewPhotoUpload';
import { Button } from '../ui/Button';
import { FormSection } from '../ui/FormSection';
import type { ReviewPhotoUploadStates } from '../../hooks/useReviewPhotoUpload';

export interface ExperienceReviewFormData {
  ratings: {
    serviceAndStaff: number;
    location: number;
    wines: number;
    food?: number;
    value: number;
  };
  title: string;
  body: string;
  photos: ReviewPhoto[];
}

export interface ExperienceReviewFormProps {
  initialData?: Partial<ExperienceReviewFormData>;
  includesFood?: boolean;
  onSubmit: (data: ExperienceReviewFormData) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  /** Per-photo upload progress reported by the parent while the review is submitting. */
  photoUploadStates?: ReviewPhotoUploadStates;
  disabled?: boolean;
  className?: string;
}

const DEFAULT_DATA: ExperienceReviewFormData = {
  ratings: {
    serviceAndStaff: 0,
    location: 0,
    wines: 0,
    value: 0,
  },
  title: '',
  body: '',
  photos: [],
};

export const ExperienceReviewForm = ({
  initialData,
  includesFood = false,
  onSubmit,
  onCancel,
  isSubmitting = false,
  photoUploadStates,
  disabled = false,
  className = '',
}: ExperienceReviewFormProps) => {
  const [formData, setFormData] = useState<ExperienceReviewFormData>({
    ...DEFAULT_DATA,
    ...initialData,
    ratings: { ...DEFAULT_DATA.ratings, ...initialData?.ratings },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleRatingChange = useCallback((field: keyof ExperienceReviewFormData['ratings'], value: number) => {
    setFormData((prev) => ({
      ...prev,
      ratings: { ...prev.ratings, [field]: value },
    }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  }, []);

  const handleTitleChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, title: value }));
    setErrors((prev) => ({ ...prev, title: '' }));
  }, []);

  const handleBodyChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, body: value }));
    setErrors((prev) => ({ ...prev, body: '' }));
  }, []);

  const handlePhotosChange = useCallback((photos: ReviewPhoto[]) => {
    setFormData((prev) => ({ ...prev, photos }));
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate ratings
    if (formData.ratings.serviceAndStaff === 0) {
      newErrors.serviceAndStaff = 'Please rate Service & Staff';
    }
    if (formData.ratings.location === 0) {
      newErrors.location = 'Please rate Location';
    }
    if (formData.ratings.wines === 0) {
      newErrors.wines = 'Please rate Wines';
    }
    if (formData.ratings.value === 0) {
      newErrors.value = 'Please rate Value';
    }
    // Food rating is optional even when includesFood is true

    // Validate title
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 120) {
      newErrors.title = 'Title must be 120 characters or less';
    }

    // Validate body
    if (!formData.body.trim()) {
      newErrors.body = 'Review body is required';
    } else if (formData.body.length < 100) {
      newErrors.body = 'Review must be at least 100 characters';
    } else if (formData.body.length > 20000) {
      newErrors.body = 'Review must be 20,000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const [submitError, setSubmitError] = useState('');

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    setSubmitError('');
    try {
      await onSubmit(formData);
    } catch (error) {
      // Photo uploads happen during submission, so a failure here is the only
      // place the user learns that an image never made it to the server.
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Something went wrong while submitting your review. Please try again.'
      );
    }
  }, [validate, onSubmit, formData]);

  const isFormDisabled = disabled || isSubmitting;

  const overallRating = useMemo(() => {
    const { serviceAndStaff, location, wines, food, value } = formData.ratings;
    const ratings = [serviceAndStaff, location, wines, value];
    if (food && food > 0) {
      ratings.push(food);
    }
    const validRatings = ratings.filter((r) => r > 0);
    if (validRatings.length === 0) return 0;
    return validRatings.reduce((sum, r) => sum + r, 0) / validRatings.length;
  }, [formData.ratings]);

  return (
    <div className={className}>
      {/* Ratings Section */}
      <FormSection title="Rate Your Experience" titleAs="h3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StarRating
            label="Service & Staff"
            value={formData.ratings.serviceAndStaff}
            onChange={(v: number) => handleRatingChange('serviceAndStaff', v)}
            disabled={isFormDisabled}
            error={errors.serviceAndStaff}
            required
          />
          <StarRating
            label="Location"
            value={formData.ratings.location}
            onChange={(v: number) => handleRatingChange('location', v)}
            disabled={isFormDisabled}
            error={errors.location}
            required
          />
          <StarRating
            label="Wines"
            value={formData.ratings.wines}
            onChange={(v: number) => handleRatingChange('wines', v)}
            disabled={isFormDisabled}
            error={errors.wines}
            required
          />
          <StarRating
            label="Value"
            value={formData.ratings.value}
            onChange={(v: number) => handleRatingChange('value', v)}
            disabled={isFormDisabled}
            error={errors.value}
            required
          />
          {includesFood && (
            <StarRating
              label="Food"
              value={formData.ratings.food ?? 0}
              onChange={(v: number) => handleRatingChange('food', v)}
              disabled={isFormDisabled}
              error={errors.food}
            />
          )}
        </div>

        {overallRating > 0 && (
          <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
            <span className="text-sm font-medium text-gray-700">Overall:</span>
            <span className="text-lg font-bold text-yellow-500">{overallRating.toFixed(1)}</span>
            <span className="text-sm text-gray-500">/ 5</span>
          </div>
        )}
      </FormSection>

      {/* Text Fields Section */}
      <ReviewTextFields
        title={formData.title}
        body={formData.body}
        onTitleChange={handleTitleChange}
        onBodyChange={handleBodyChange}
        disabled={isFormDisabled}
        titleError={errors.title}
        bodyError={errors.body}
      />

      {/* Photo Upload Section */}
      <ReviewPhotoUpload
        photos={formData.photos}
        onPhotosChange={handlePhotosChange}
        disabled={isFormDisabled}
        uploadStates={photoUploadStates}
      />

      {submitError && (
        <div
          className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm"
          role="alert"
        >
          <p>{submitError}</p>
          <p className="mt-1 text-red-600">
            Photos that already uploaded will not be sent again.
          </p>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isFormDisabled}
            className="mt-2 font-medium underline hover:no-underline disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
          >
            Try again
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex gap-3 border-t border-gray-200 pt-6">
        <Button variant="primary" onClick={handleSubmit} disabled={isFormDisabled} loading={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit Review'}
        </Button>
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};
